import { AbortError } from "./abortError";
import { ACQUIRED } from "./constants";
import { RetryError } from "./retryError";
import { waitUntil } from "./wait";
import { TimeoutError } from "./timeoutError";

/**
 * Optional settings that control the behavior of a wait operation.
 */
export interface ThrottlerWaitOptions<T extends Throttler = Throttler> {
  /**
   * An `AbortSignal` that allows the wait to be cancelled early.
   *
   * If the signal is already aborted or is aborted before the wait completes,
   * the returned promise will reject with an `AbortError`.
   */
  signal?: AbortSignal;

  /**
   *  A callback invoked before each retry.
   *
   * Receives the time to wait before the operation will be retried, 
   * in milliseconds. If the callback returns `false`, the wait will be 
   * aborted and a `RetryError` will be thrown.
   *
   * @param ms - The time to wait for the next retry attempt, in milliseconds.
   *
   * @returns `false` to cancel the retry; any other value continues retrying.
   */
  onRetry?: (ms: number, throttler: T) => unknown;

  /**
   * Maximum duration to wait before timing out, in milliseconds.
   *
   * If the wait has not completed within this duration, the returned promise
   * will reject with a `TimeoutError`.
   */
  timeout?: number;
}

/**
 * An interface for throttlers that regulate the pacing
 * of operations to conform to a specified rate.
 */
export abstract class Throttler {
  /**
   * Attempts to acquire permission to proceed with a request.
   *
   * If the request is allowed, this should return {@link ACQUIRED}.
   *
   * Otherwise, it should return a timestamp (from `performance.now()`)
   * indicating when the caller should retry.
   */
  protected abstract tryAcquire(): number;

  /**
   * Attempts to proceed immediately without waiting.
   *
   * Performs a non-blocking check to determine whether the operation
   * can be executed immediately based on the throttler's state.
   *
   * @returns `true` if the operation can proceed immediately,
   * `false` if it must be delayed.
   */
  tryWait(): boolean {
    return this.tryAcquire() === ACQUIRED;
  }

  /**
   * Waits until the operation is allowed to proceed.
   *
   * Blocks until the throttler permits execution based on its
   * rate-limiting logic. Supports optional timeout, abort signal,
   * and a retry callback that can cancel retries.
   *
   * @param options Optional settings for aborting, timing out, or
   *                controlling retry behavior.
   *
   * @returns A promise that resolves when the caller is permitted to proceed,
   *          or rejects if aborted, timed out, or the retry is cancelled.
   *
   * @throws {AbortError} if the provided signal is aborted before resolution.
   *
   * @throws {RetryError} if the retry is cancelled by the `onRetry` callback returning `false`.
   *
   * @throws {TimeoutError} if the wait exceeds the specified timeout.
   */
  async wait({ signal, onRetry, timeout }: ThrottlerWaitOptions = {}): Promise<void> {
    let timeoutId: ReturnType<typeof setTimeout>;

    const cleanup = () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      signal?.removeEventListener("abort", onAbort);
    };

    const onAbort = () => {
      cleanup();
      throw new AbortError();
    };

    const onTimeout = () => {
      cleanup();
      throw new TimeoutError();
    };

    if (signal?.aborted) {
      throw new AbortError();
    }

    signal?.addEventListener("abort", onAbort);
    if (timeout != null) {
      timeoutId = setTimeout(onTimeout, timeout);
    }

    try {
      while (true) {
        const ts = this.tryAcquire();
        if (ts === ACQUIRED) {
          break;
        }
        if (onRetry?.(ts, this) === false) {
          throw new RetryError();
        }
        await waitUntil(ts);
      }
    } finally {
      cleanup();
    }
  }
}
